/**
 * Variable Provider
 *
 * Unified system for building template variables from multiple data sources.
 * Supports standard variables, client-configured variables, and computed formulas.
 *
 * =============================================================================
 * DATA SOURCES (in priority order, later sources override earlier)
 * =============================================================================
 * 1. Default/legacy data (basis, interest, etc.)
 * 2. Calculator result (breakdown per-class, fundMetrics, etc.)
 * 3. Client config (clientData.templateVariables)
 * 4. Class config (clientData.equity[key].templateVariables)
 * 5. Aggregates (calculated totals from cap table)
 * 6. Calculator customVariables (for extension)
 *
 * =============================================================================
 * VARIABLE CONFIG FORMAT
 * =============================================================================
 * {
 *   "variableName": {
 *     "source": "path.to.value" | "aggregate.field",
 *     "type": "currency" | "number" | "percentage" | "raw",
 *     "formula": "var1 / var2 * 100"  // optional computed value
 *   }
 * }
 */

import * as Sentry from '@sentry/browser';
import { TemplateUtils } from '../helpers/TemplateUtils.js';
import { resolveColumnValue, mapCustomColumnFormatToType } from '../helpers/field-engine/ColumnResolvers.js';
import { resolveClassTotalShares } from '../helpers/class-aggregates.js';
import { isManualCapTableCustomColumnSource, readCapTableManualColumnFromUserClient } from '../../admin/custom-reports/cap-table-custom-column-storage-keys.js';

// Shared with FieldRegistry.generatePerClassFields() (#6053) -- that function
// generates the per-class variable *keys* the template picker inserts, and this
// function generates the *namespace* buildCampaignVariables() actually resolves
// at publish time. They must produce identical output for the same class name,
// or the picker inserts a placeholder that can never be substituted. Collapses
// consecutive non-alnum characters to a single "_" rather than replacing each
// one individually, so "SPB - H Units" reads as "SPB_H_Units" instead of the
// much less readable "SPB___H_Units".
export function sanitizeClassKey(className) {
	return String(className || '').replace(/[^a-zA-Z0-9]+/g, '_');
}

const VariableProvider = {
	// ========================================
	// STANDARD VARIABLE TYPES
	// ========================================
	// Base types that are always available. Client config can add more.

	standardTypes: {
		// Share counts
		owned: 'number',
		approved: 'number',
		vested: 'number',
		unvested: 'number',
		orig: 'number',
		tranches: 'number',

		// Currency values
		committed: 'currency',
		basis: 'currency',
		value: 'currency',
		interest: 'currency',
		uncalled: 'currency',
		vestedValue: 'currency',
		unvestedValue: 'currency',
		sharePrice: 'currency',
		strikePrice: 'currency',

		// Income allocations
		taxBasis: 'currency',
		incomeAllocations: 'currency',
		contributions: 'currency',
		distributions: 'currency',
		marketValue: 'currency',
		currentValue: 'currency',
		unrealizedGain: 'currency',

		// Percentage values
		vestedPercent: 'percentage',
		ownershipPercent: 'percentage',

		// Multipliers
		tvpi: 'multiplier',

		// Text/labels
		name: 'raw',
		sharesType: 'raw',
		youownLabel: 'raw',
		costbasisLabel: 'raw',
		uncalledLabel: 'raw',
		tradeAction: 'raw',
		expDate: 'raw',
		stockStatus: 'raw',
		unitholderID: 'raw',
		userid: 'raw',

		// Booleans
		hasVesting: 'raw',
		isDebt: 'raw',
		hasCalculator: 'raw',

		// Aggregates
		totalClassShares: 'number',
		totalClassValue: 'currency',
		shareholderCount: 'number',

		// Numeric (eligibility, etc.)
		eligible: 'number',
	},

	// ========================================
	// SOURCE PATH RESOLUTION
	// ========================================

	/**
	 * Safely resolve a dot-notation path from an object
	 * e.g., "userData.clients.foo.status" -> userData.clients.foo.status value
	 *
	 * @param {Object} obj - Source object
	 * @param {string} path - Dot-notation path
	 * @param {Object} context - Context with special keys like {client}
	 * @returns {*} - Resolved value or undefined
	 */
	resolvePath: function (obj, path, context = {}) {
		if (!obj || !path || typeof path !== 'string') return undefined;

		// Replace placeholders with actual values
		if (context.client) {
			path = path.replace(/\{client\}/g, context.client);
		}
		if (context.classKey) {
			path = path.replace(/\{classKey\}/g, context.classKey);
		}

		const parts = path.split('.');
		let current = obj;

		for (const part of parts) {
			if (current === undefined || current === null) return undefined;
			if (!Object.prototype.hasOwnProperty.call(current, part)) return undefined;
			current = current[part];
		}

		return current;
	},

	/**
	 * Evaluate a simple formula with variable substitution
	 * Supports: +, -, *, /
	 * Example: "(owned / totalClassShares) * 100"
	 *
	 * @param {string} formula - Formula string
	 * @param {Object} vars - Current variable map
	 * @returns {number|null} - Computed value or null on error
	 */
	evaluateFormula: function (formula, vars) {
		if (!formula || typeof formula !== 'string') return null;

		try {
			// Replace variable names with their numeric values
			// Match word characters that aren't operators
			let expression = formula;

			// Extract all variable references (alphanumeric + underscore)
			const varNames = formula.match(/[a-zA-Z_][a-zA-Z0-9_]*/g) || [];

			for (const varName of varNames) {
				const value = TemplateUtils.safeGetVar(vars, varName);
				if (value === undefined || value === null) return null;

				const numValue = Number(value);
				if (isNaN(numValue)) return null;

				// Replace the variable with its numeric value
				// Use word boundary to avoid partial replacements
				const regex = new RegExp('\\b' + varName + '\\b', 'g');
				expression = expression.replace(regex, numValue.toString());
			}

			// Validate: only allow numbers, operators, parentheses, spaces, dots
			if (!/^[\d\s+\-*/().]+$/.test(expression)) {
				console.warn('[VariableProvider] Invalid formula expression:', formula);
				return null;
			}

			// Evaluate the expression safely
			// Using Function constructor is safer than eval when input is validated
			const result = Function('"use strict"; return (' + expression + ')')();

			if (typeof result !== 'number' || isNaN(result) || !isFinite(result)) {
				return null;
			}

			return result;
		} catch (e) {
			console.warn('[VariableProvider] Error evaluating formula:', formula, e);
			return null;
		}
	},

	// ========================================
	// VARIABLE BUILDING (scaffolding for future HeadlineFormatter integration)
	// ========================================
	// NOTE: buildClassVariables and buildPortfolioVariables are currently unused.
	// EquityCardFormatter.buildVariableMap handles variable building directly.
	// These methods are retained for potential HeadlineFormatter integration.

	/**
	 * Build variables for equity card templates
	 *
	 * @param {Object} context - All data sources
	 * @param {Object} context.userShares - User's share data for this class
	 * @param {Object} context.equityConfig - Client's equity config for this class
	 * @param {Object} context.calculatorData - Per-class calculator breakdown
	 * @param {Object} context.aggregates - Cap table aggregates for this class
	 * @param {Object} context.userData - Full user data object
	 * @param {Object} context.clientData - Full client data object
	 * @param {string} context.classKey - Current share class key
	 * @param {Object} context.legacy - Legacy values (basis, value, interest)
	 * @returns {Object} - { vars: Object, types: Object }
	 */
	buildClassVariables: function (context) {
		const {
			userShares = {},
			equityConfig = {},
			calculatorData = {},
			aggregates = {},
			userData = {},
			clientData = {},
			classKey = '',
			legacy = {},
		} = context;

		const vars = {};
		const types = { ...this.standardTypes };

		// 1. Legacy/default values
		if (legacy.basis !== undefined) vars.basis = Number(legacy.basis) || 0;
		if (legacy.value !== undefined) vars.value = Number(legacy.value) || 0;
		if (legacy.interest !== undefined) vars.interest = Number(legacy.interest) || 0;

		// 2. User share data
		vars.owned = Number(userShares.owned || 0);
		vars.approved = Number(userShares.approved || 0);
		vars.committed = Number(userShares.committed || 0);
		vars.orig = Number(userShares.orig || 0);
		vars.tranches = Number(userShares.tranches || 0);

		// 3. Equity config labels
		vars.name = equityConfig.name || classKey;
		vars.sharesType = equityConfig.sharesType || 'shares';
		vars.youownLabel = equityConfig.youownLabel || 'You Own';
		vars.costbasisLabel = equityConfig.costbasisLabel || 'Cost Basis';
		vars.uncalledLabel = equityConfig.uncalledLabel || 'Uncalled';

		// 4. Stock status from userData (Fishbeck pattern)
		const client = clientData.code || clientData.name || '';
		if (userData.clients && userData.clients[client]) {
			vars.stockStatus = userData.clients[client].status || '';

			// Calculate eligible based on stock status
			const statusStr = String(vars.stockStatus || '');
			const statusNum = parseFloat(statusStr);
			if (!isNaN(statusNum) && statusNum > 0) {
				vars.eligible = statusNum;
			} else {
				vars.eligible = vars.owned;
			}
		}

		// 5. User ID
		vars.userid = userData.userid || '';
		vars.unitholderID = userData.userid || '';

		// 6. Calculator data (per-class breakdown)
		if (calculatorData) {
			if (calculatorData.value !== undefined) vars.value = Number(calculatorData.value);
			if (calculatorData.vested !== undefined) vars.vested = Number(calculatorData.vested);
			if (calculatorData.vestedValue !== undefined) vars.vestedValue = Number(calculatorData.vestedValue);
			if (calculatorData.unvestedValue !== undefined) vars.unvestedValue = Number(calculatorData.unvestedValue);
			if (calculatorData.sharePrice !== undefined) vars.sharePrice = Number(calculatorData.sharePrice);
			if (calculatorData.strikePrice !== undefined) vars.strikePrice = Number(calculatorData.strikePrice);

			// Income allocations variables
			if (calculatorData.taxBasis !== undefined) vars.taxBasis = Number(calculatorData.taxBasis);
			if (calculatorData.incomeAllocations !== undefined) vars.incomeAllocations = Number(calculatorData.incomeAllocations);
			if (calculatorData.marketValue !== undefined) vars.marketValue = Number(calculatorData.marketValue);
			if (calculatorData.currentValue !== undefined) vars.currentValue = Number(calculatorData.currentValue);
			if (calculatorData.unrealizedGain !== undefined) vars.unrealizedGain = Number(calculatorData.unrealizedGain);
			if (calculatorData.contributions !== undefined) vars.contributions = Number(calculatorData.contributions);
			if (calculatorData.distributions !== undefined) vars.distributions = Number(calculatorData.distributions);

			// TVPI for fund classes
			if (calculatorData.tvpi !== undefined) vars.tvpi = Number(calculatorData.tvpi);

			// Calculator custom variables
			if (calculatorData.customVariables) {
				Object.entries(calculatorData.customVariables).forEach(([key, value]) => {
					vars[key] = value;
					// Infer type if not already defined
					if (!types[key]) {
						types[key] = typeof value === 'number' ? 'number' : 'raw';
					}
				});
			}
		}

		// Calculate unvested if we have vested
		if (vars.vested !== undefined) {
			vars.unvested = Math.max(0, vars.owned - vars.vested);
		}

		// Calculate vested percent
		if (vars.vested !== undefined && vars.owned > 0) {
			vars.vestedPercent = (vars.vested / vars.owned) * 100;
		}

		// Boolean flags
		vars.hasVesting = vars.vested !== undefined && vars.vested !== vars.owned;
		vars.hasCalculator = !!calculatorData && Object.keys(calculatorData).length > 0;
		vars.isDebt = equityConfig.debt === true;

		// 7. Aggregate data (cap table totals)
		if (aggregates) {
			if (aggregates.totalOwned !== undefined) {
				vars.totalClassShares = Number(aggregates.totalOwned);
			}
			if (aggregates.totalValue !== undefined) {
				vars.totalClassValue = Number(aggregates.totalValue);
			}
			if (aggregates.shareholderCount !== undefined) {
				vars.shareholderCount = Number(aggregates.shareholderCount);
			}
		}

		// 8. Client-configured template variables (clientData.templateVariables)
		if (clientData.templateVariables) {
			this._applyConfiguredVariables(vars, types, clientData.templateVariables, {
				userData,
				clientData,
				aggregates,
				client,
			});
		}

		// 9. Class-configured template variables (equityConfig.templateVariables)
		if (equityConfig.templateVariables) {
			this._applyConfiguredVariables(vars, types, equityConfig.templateVariables, {
				userData,
				clientData,
				aggregates,
				client,
				vars, // Pass current vars for formula evaluation
			});
		}

		// 10. Compute ownershipPercent if we have the data
		if (vars.owned !== undefined && vars.totalClassShares && vars.totalClassShares > 0) {
			vars.ownershipPercent = (vars.owned / vars.totalClassShares) * 100;
		}

		return { vars, types };
	},

	/**
	 * Apply configured variables from templateVariables config
	 *
	 * @param {Object} vars - Variable map to populate
	 * @param {Object} types - Type map to populate
	 * @param {Object} config - templateVariables config object
	 * @param {Object} sources - Data sources for path resolution
	 */
	_applyConfiguredVariables: function (vars, types, config, sources) {
		if (!config || typeof config !== 'object') return;

		const { userData, clientData, aggregates, client, vars: currentVars, userClient, userShares, equityConfig } = sources;

		Object.entries(config).forEach(([varName, varConfig]) => {
			// Validate variable name
			if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(varName)) {
				console.warn('[VariableProvider] Invalid variable name in config:', varName);
				return;
			}

			// Support shorthand string config (value is the source path)
			if (typeof varConfig === 'string') {
				varConfig = { source: varConfig };
			}

			if (typeof varConfig !== 'object' || varConfig === null) return;

			const { source, type, formula } = varConfig;

			// Register type
			if (type) {
				types[varName] = type;
			}

			// Compute from formula
			if (formula) {
				// Use current vars merged with any new vars we've set
				const mergedVars = { ...vars, ...(currentVars || {}) };
				const computed = this.evaluateFormula(formula, mergedVars);
				if (computed !== null) {
					vars[varName] = computed;
				}
				return;
			}

			// Resolve from source path
			if (source) {
				let value;

				// Handle special source prefixes
				// Shorthand prefixes (user., shares., config.) — available when caller provides those sources
				if (source.startsWith('user.') && userClient) {
					value = this.resolvePath(userClient, source.replace('user.', ''), { client });
				} else if (source.startsWith('shares.') && userShares) {
					value = this.resolvePath(userShares, source.replace('shares.', ''), { client });
				} else if (source.startsWith('config.') && equityConfig) {
					value = this.resolvePath(equityConfig, source.replace('config.', ''), { client });
				} else if (source.startsWith('userData.')) {
					value = this.resolvePath(userData, source.replace('userData.', ''), { client });
				} else if (source.startsWith('clientData.')) {
					value = this.resolvePath(clientData, source.replace('clientData.', ''), { client });
				} else if (source.startsWith('aggregate.') || source.startsWith('aggregates.')) {
					const aggPath = source.replace(/^aggregates?\./, '');
					value = this.resolvePath(aggregates, aggPath, { client });
				} else {
					// Direct path (could be any source)
					value = this.resolvePath({ userData, clientData, aggregates }, source, { client });
				}

				if (value !== undefined) {
					vars[varName] = value;
				}
			}
		});
	},

	// ========================================
	// PORTFOLIO-LEVEL VARIABLES
	// ========================================

	/**
	 * Build variables for portfolio/headline templates
	 * Similar to buildClassVariables but for portfolio-wide context
	 *
	 * @param {Object} context - Data sources
	 * @param {Object} context.calculatorResult - Full calculator result
	 * @param {Object} context.userData - User data
	 * @param {Object} context.clientData - Client data
	 * @param {Object} context.legacyData - Legacy portfolio totals
	 * @returns {Object} - { vars: Object, types: Object }
	 */
	buildPortfolioVariables: function (context) {
		const { calculatorResult = {}, userData = {}, clientData = {}, legacyData = {} } = context;

		const vars = {};
		const types = { ...this.standardTypes };

		// Legacy portfolio values
		if (legacyData.totalPaidInCapital !== undefined) vars.totalPaidInCapital = Number(legacyData.totalPaidInCapital);
		if (legacyData.totalCurrentValue !== undefined) vars.totalCurrentValue = Number(legacyData.totalCurrentValue);
		if (legacyData.totalDistributions !== undefined) vars.totalDistributions = Number(legacyData.totalDistributions);
		if (legacyData.totalCash !== undefined) vars.totalCash = Number(legacyData.totalCash);

		// Calculator results
		if (calculatorResult.totalValue !== undefined) vars.totalValue = Number(calculatorResult.totalValue);
		if (calculatorResult.vestedValue !== undefined) vars.vestedValue = Number(calculatorResult.vestedValue);
		if (calculatorResult.unvestedValue !== undefined) vars.unvestedValue = Number(calculatorResult.unvestedValue);
		if (calculatorResult.totalContributions !== undefined) vars.totalContributions = Number(calculatorResult.totalContributions);
		if (calculatorResult.totalDistributions !== undefined) vars.totalDistributions = Number(calculatorResult.totalDistributions);

		// Fund metrics
		if (calculatorResult.fundMetrics) {
			const fm = calculatorResult.fundMetrics;
			if (fm.tvpi !== undefined) vars.tvpi = Number(fm.tvpi);
			if (fm.dpi !== undefined) vars.dpi = Number(fm.dpi);
			if (fm.rvpi !== undefined) vars.rvpi = Number(fm.rvpi);
			if (fm.moic !== undefined) vars.moic = Number(fm.moic);
			if (fm.pic !== undefined) vars.pic = Number(fm.pic);
		}

		// Calculator custom variables
		if (calculatorResult.customVariables) {
			Object.entries(calculatorResult.customVariables).forEach(([key, value]) => {
				vars[key] = value;
				if (!types[key]) {
					types[key] = typeof value === 'number' ? 'number' : 'raw';
				}
			});
		}

		// User/client data
		const client = clientData.code || clientData.name || '';
		vars.userid = userData.userid || '';
		vars.unitholderID = userData.userid || '';

		if (userData.clients && userData.clients[client]) {
			vars.stockStatus = userData.clients[client].status || '';
		}

		// Labels
		vars.labelPaidIn = clientData.language?.labelPaidIn || 'Paid-In Capital';
		vars.accountType = clientData.language?.accountType || '';

		// Client-configured variables
		if (clientData.templateVariables) {
			this._applyConfiguredVariables(vars, types, clientData.templateVariables, {
				userData,
				clientData,
				client,
				vars,
			});
		}

		return { vars, types };
	},

	/**
	 * Get the type for a variable, checking dynamic types first
	 *
	 * @param {string} varName - Variable name
	 * @param {Object} dynamicTypes - Additional types from config
	 * @returns {string} - Variable type
	 */
	getType: function (varName, dynamicTypes = {}) {
		return dynamicTypes[varName] || this.standardTypes[varName] || 'raw';
	},

	// ========================================
	// CAMPAIGN-LEVEL VARIABLES
	// ========================================

	/**
	 * Build variables for a multi-class context (campaigns, multi-class views).
	 *
	 * Calls buildClassVariables for each class and namespaces the results
	 * by class key (e.g. owned_PBTF, name_TPBC). Adds cross-class aggregates
	 * and a composite share_table. Uses _applyConfiguredVariables for any
	 * config overrides — same path as dashboard/equity cards.
	 *
	 * @param {Object} context
	 * @param {string[]} context.campaignClasses - Expanded class keys (e.g. ['WEI-C','WEI-P'])
	 * @param {Object}   context.userShares      - userData.clients[client].shares
	 * @param {Object}   context.clientEquity     - clientData.equity
	 * @param {string}   context.client           - Client code
	 * @param {Object}  [context.userData]        - Full user data object
	 * @param {Object}  [context.clientData]      - Full client data object
	 * @param {Object}  [context.calculationResult] - Calculator result from computeShareholderPreview (includes per-class breakdown)
	 * @param {Object}  [context.campaignConfig]  - activeCampaign.templateVariables overrides
	 * @returns {Object} - { vars: Object, types: Object }
	 */
	buildCampaignVariables: function (context) {
		const {
			campaignClasses = [],
			userShares = {},
			clientEquity = {},
			client = '',
			userData = {},
			clientData = {},
			campaignConfig,
			calculationResult,
		} = context;

		const vars = {};
		const types = {};
		const userClient = userData.clients?.[client] || {};

		// ---- 1. User-level variables (from userData, not class-specific) ----
		// buildClassVariables exposes userid/stockStatus/eligible but not
		// user_name or arbitrary userClient fields — add those here.
		const userName = userData.user_name || '';
		vars.user_name = userName;
		vars.full_name = userName;
		vars.shareholder_name = userName;
		vars.FULL_NAME = userName.toUpperCase();
		vars.userid = userData.userid || '';
		vars.email_address = userData.userid || '';
		for (const [key, value] of Object.entries(userClient)) {
			if (key === 'shares' || key === 'documents' || key === 'alias' || key === 'investor') continue;
			if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
				vars[key] = value;
			}
		}

		// ---- 2. Build per-class variables via buildClassVariables ----
		const classData = [];
		let totalOwned = 0;

		// Extract per-class calculator breakdown if available
		const calcBreakdown = {};
		if (calculationResult?.breakdown) {
			calculationResult.breakdown.forEach((cls) => {
				if (cls.classKey) calcBreakdown[cls.classKey] = cls;
			});
		}

		for (const cls of campaignClasses) {
			const classResult = this.buildClassVariables({
				userShares: userShares[cls] || {},
				equityConfig: clientEquity[cls] || {},
				calculatorData: calcBreakdown[cls] || {},
				userData,
				clientData,
				classKey: cls,
			});

			const owned = classResult.vars.owned || 0;
			const name = classResult.vars.name || cls;
			const displayName = name !== cls ? `${name} (${cls})` : cls;

			classData.push({ classKey: cls, name, displayName, owned });
			totalOwned += owned;

			// Namespace all per-class vars using CLASS_field format
			// (matches FieldRegistry.generatePerClassFields convention: ONW_owned, WEI_C_value)
			const safeKey = sanitizeClassKey(cls);
			for (const [field, value] of Object.entries(classResult.vars)) {
				if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
					vars[`${safeKey}_${field}`] = value;
					if (classResult.types[field]) types[`${safeKey}_${field}`] = classResult.types[field];
				}
			}

			// Display name convenience variable
			vars[`${safeKey}_share_type`] = displayName;
			types[`${safeKey}_share_type`] = 'raw';
		}

		// ---- 3. Aggregate share variables ----
		vars.share_count = totalOwned;
		vars.share_count_total = totalOwned;
		vars.amount = totalOwned;
		types.share_count = 'number';
		types.share_count_total = 'number';
		types.amount = 'number';

		// ---- 4. Composite share_table ----
		vars.share_table = this._renderShareTable(classData, totalOwned);
		types.share_table = 'raw';

		// ---- 5. Context for path-based resolution (same as EquityCardFormatter) ----
		vars._context = {
			user: userClient,
			shares: userShares,
			config: clientEquity,
			userData: userData,
			clientData: clientData,
		};

		// ---- 6. Config overrides via existing _applyConfiguredVariables ----
		if (campaignConfig && typeof campaignConfig === 'object') {
			this._applyConfiguredVariables(vars, types, campaignConfig, {
				userData,
				clientData,
				client,
				vars,
				userClient,
				userShares,
				equityConfig: clientEquity,
			});
		}

		// ---- 7. Custom cap table columns (#6083) ----
		// Template pickers insert {clientData.cap_table_custom_columns[i].key}
		// verbatim (see FieldRegistry.generateCustomColumnFields) -- resolve those
		// same keys here, same infrastructure HeadlineFormatter uses for headlines
		// (commit 7f30ed670). proxy-voting.js substitutes every key in this map
		// (see its `for (let key in substitutionMap)` loop), so a formula/manual
		// custom column now substitutes there instead of leaving the raw
		// {formula_...} placeholder visible to the voter. signature.js also calls
		// buildCampaignVariables, but only consumes buildPerClassSubstitutionMap's
		// per-class-prefixed subset -- a bare custom-column key added here is NOT
		// yet reached by signature.js's own substitution loop, and enroll.js does
		// not call buildCampaignVariables at all. Extending those is a separate,
		// unreported follow-up, not part of #6083's voting-campaign repro.
		this._resolveCustomColumns(vars, types, { userData, clientData, client, campaignClasses, userShares, clientEquity, totalOwned });

		return { vars, types };
	},

	/**
	 * Resolve clientData.cap_table_custom_columns and inject any that don't
	 * collide with an existing variable. Mirrors
	 * HeadlineFormatter._resolveCustomColumns's manual/formula/aggregate/
	 * calculator/direct source handling.
	 *
	 * @param {Object} vars - Variable map to populate (mutated in place)
	 * @param {Object} types - Type map to populate (mutated in place)
	 * @param {Object} options
	 * @param {Object} options.userData
	 * @param {Object} options.clientData
	 * @param {string} options.client
	 * @param {string[]} options.campaignClasses - Classes this campaign is scoped to
	 * @param {Object} options.userShares - userData.clients[client].shares
	 * @param {Object} options.clientEquity - clientData.equity
	 * @param {number} options.totalOwned - Shares owned across campaignClasses (for ownership_percent-style formulas)
	 */
	_resolveCustomColumns: function (vars, types, { userData, clientData, client, campaignClasses, userShares, clientEquity, totalOwned }) {
		const columns = clientData?.cap_table_custom_columns;
		if (!Array.isArray(columns)) return;

		const userClient = userData?.clients?.[client];
		if (!userClient) return;

		const shareClasses = campaignClasses || [];

		// Only sum resolveClassTotalShares (no-cap-table-required, #5838) when a
		// column actually needs it -- it can fall back to a full cap table scan
		// per class, which would otherwise run on every render for no reason.
		// A formula/expression column can reference 'ownership_percent' (or any
		// other calculator-backed field) indirectly, so any non-manual source is
		// treated as a potential consumer -- only skip when every column is
		// manually-entered (the common case).
		const needsTotalShares = columns.some((col) => !isManualCapTableCustomColumnSource(col?.source));
		const totalShares = needsTotalShares ? shareClasses.reduce((sum, cls) => sum + (resolveClassTotalShares(cls, { clientData, client }) || 0), 0) : 0;

		// userEvents/clientEvents/shareClasses feed columnResolvers.aggregate
		// (sumEventsInRange short-circuits to 0 without them) for 'aggregate:'-
		// sourced columns, matching the context HeadlineFormatter builds.
		const userEvents = [];
		const clientEvents = [];
		shareClasses.forEach((cls) => {
			const classShares = userShares?.[cls];
			if (classShares?.events) userEvents.push(...classShares.events);
			const classEvents = clientEquity?.[cls]?.events;
			if (classEvents) clientEvents.push(...classEvents);
		});

		const resolverContext = {
			user: userData,
			userClient,
			userid: userData?.userid,
			shares: { owned: totalOwned },
			owned: totalOwned,
			totalShares,
			userEvents,
			clientEvents,
			shareClasses,
			clientData,
		};

		columns.forEach((col) => {
			if (!col?.key) return;

			const isManual = isManualCapTableCustomColumnSource(col.source);
			// Non-manual (computed) sources still skip on collision -- a formula
			// could genuinely collide with a real builtin/aggregate var name.
			// Manual columns never skip here: their canonical storage IS
			// userClient[col.key], so step 1's raw userClient scalar-copy loop
			// above has typically already set vars[col.key] to this exact value
			// with no type applied -- re-processing is what attaches col.format's
			// currency/number/percentage coercion, not a clobber of a different var.
			if (!isManual && vars[col.key] !== undefined) return;

			let value;
			if (isManual) {
				value = readCapTableManualColumnFromUserClient(userClient, col.key, col.label);
			} else {
				try {
					value = resolveColumnValue({ key: col.key, source: col.source }, resolverContext, {});
				} catch (e) {
					// console.warn is stripped by Terser in production builds -- use
					// Sentry so a broken admin-configured column has a diagnostic trail
					// (matches the pattern in helpers/bulk-downloader.js).
					Sentry.captureMessage('VariableProvider: error resolving custom column', {
						level: 'warning',
						extra: { columnKey: col.key, source: col.source, client, errorMessage: e?.message || String(e) },
						fingerprint: ['variable-provider-custom-column-error', col.key],
					});
					return;
				}
			}

			// A resolver that produced genuinely nothing (threw, already handled
			// above) leaves `value` undefined -- skip entirely. But a resolved
			// blank (empty string, or a calculator's intentional `null`) must
			// still register as '' rather than be omitted: proxy-voting.js's
			// ONE_FOR_ALL bulk-sign loop reuses one HTML string's <!--{key}-->
			// markers across every account in sequence, replacing only the
			// markers present in each account's own substitution map. Omitting
			// the key for an account whose column resolves blank would leave a
			// PRIOR account's value baked into that marker -- a cross-shareholder
			// data leak on the saved PDF, not just a blank field (#6083).
			if (value === undefined) return;
			if (value === null) value = '';

			const type = mapCustomColumnFormatToType(col.format);
			if (type !== 'raw' && typeof value === 'string' && value.trim() !== '') {
				const num = Number(value.replace(/,/g, ''));
				if (!isNaN(num)) value = num;
			}

			vars[col.key] = value;
			types[col.key] = type;
		});
	},

	/**
	 * Build a {key} -> formatted value substitution map containing ONLY the
	 * per-class namespaced variables from buildCampaignVariables (e.g. {SPB_H_Units_owned}),
	 * excluding aggregate/user-level vars (full_name, amount, share_table, etc.).
	 *
	 * Callers that already have their own, more nuanced handling for those
	 * aggregate/user-level vars (e.g. signature.js's signingAccount override for
	 * {full_name}) should use this instead of substituting the whole vars map, so a
	 * template can reference a SPECIFIC named class's amount (a class other than
	 * the one the campaign itself is scoped to) without touching existing behavior.
	 *
	 * @param {Object} campaignVarsResult - Output of buildCampaignVariables (has .vars, .types)
	 * @param {string[]} campaignClasses - The same campaignClasses array passed to buildCampaignVariables
	 * @returns {Object} Map of '{key}' -> formatted value
	 */
	buildPerClassSubstitutionMap: function (campaignVarsResult, campaignClasses) {
		const map = {};
		if (!campaignVarsResult?.vars || !Array.isArray(campaignClasses)) return map;
		const prefixes = campaignClasses.map((cls) => `${sanitizeClassKey(cls)}_`);
		for (const [key, val] of Object.entries(campaignVarsResult.vars)) {
			if (key === '_context') continue;
			if (!prefixes.some((prefix) => key.startsWith(prefix))) continue;
			const formatted =
				campaignVarsResult.types[key] === 'currency'
					? Number(val).toLocaleString('en-US', { style: 'currency', currency: 'USD' })
					: typeof val === 'number'
						? val.toLocaleString()
						: val;
			map[`{${key}}`] = formatted ?? '';
		}
		return map;
	},

	/**
	 * Render an HTML table showing per-class share breakdown.
	 *
	 * @param {Array<{classKey: string, name: string, displayName: string, owned: number}>} classData
	 * @param {number} total
	 * @returns {string} HTML string
	 */
	_renderShareTable: function (classData, total) {
		const heldClasses = classData.filter((cd) => cd.owned > 0);

		if (!heldClasses.length) return '';

		// Single class: simple inline text (no table needed)
		if (classData.length === 1) {
			const cd = classData[0];
			return `${cd.displayName}: ${cd.owned.toLocaleString()} shares`;
		}

		// Multiple classes: table with header, per-class rows, and total
		const cellStyle = 'padding:2pt 8pt 2pt 0; font-family:inherit; font-size:inherit; vertical-align:top;';
		const rightStyle = 'padding:2pt 0 2pt 8pt; font-family:inherit; font-size:inherit; text-align:right; vertical-align:top;';

		let rows = classData
			.map((cd) => `<tr><td style="${cellStyle}">${cd.displayName}</td><td style="${rightStyle}">${cd.owned.toLocaleString()}</td></tr>`)
			.join('');

		rows +=
			`<tr><td style="${cellStyle} border-top:1px solid #000; padding-top:4pt;"><strong>Total</strong></td>` +
			`<td style="${rightStyle} border-top:1px solid #000; padding-top:4pt;"><strong>${total.toLocaleString()}</strong></td></tr>`;

		return `<table style="border-collapse:collapse; font-family:inherit; font-size:inherit;">${rows}</table>`;
	},
};

export { VariableProvider };
